// =============================================================================
// Gate in front of the diary page (see src/lib/appLock.js for the setting
// shape and the session rules). Renders its children when the lock is off or
// the diary was unlocked recently; otherwise a centred card asking for
// Face ID (native app, when enabled) and/or the numeric code.
//
// The one thing it never does is trap the user: with the lock switched on but
// neither Face ID usable on this device nor a code set, the diary opens with
// a warning banner pointing at Settings. (Settings itself is not locked, so
// the lock is a privacy curtain, not a vault.)
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock, ScanFace, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import {
  normalizeLock,
  hasCode,
  isValidCode,
  verifyCode,
  biometricsSupported,
  authenticateWithBiometrics,
  isUnlocked,
  markUnlocked,
  subscribeLockState,
  MAX_CODE_LENGTH
} from '../lib/appLock';

const LockScreen = ({ lock, wantsBio, onBioUnavailable }) => {
  const { t } = useTranslation();
  const codeSet = hasCode(lock);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null); // 'wrongCode' | 'bioFailed' | null
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const attemptedRef = useRef(false);
  const inputRef = useRef(null);

  const tryBiometrics = useCallback(async () => {
    if (!wantsBio || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const res = await authenticateWithBiometrics(t('diaryLock.reason'));
    busyRef.current = false;
    setBusy(false);
    if (res.ok) {
      markUnlocked();
      return;
    }
    if (!res.available) {
      onBioUnavailable();
      return;
    }
    // Cancelled or failed: leave the button (and the code field) and wait.
    setError('bioFailed');
  }, [wantsBio, onBioUnavailable, t]);

  // Ask Face ID once when the lock screen appears in the foreground, and
  // again each time the app comes back while it is still showing. Never in
  // the background, and never in a loop after a cancel.
  useEffect(() => {
    if (!wantsBio) return undefined;
    const attempt = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (attemptedRef.current) return;
      attemptedRef.current = true;
      tryBiometrics();
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') attemptedRef.current = false;
      else attempt();
    };
    attempt();
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [wantsBio, tryBiometrics]);

  const submitCode = async (e) => {
    e.preventDefault();
    if (busyRef.current || !isValidCode(code)) return;
    busyRef.current = true;
    setBusy(true);
    const ok = await verifyCode(code, lock);
    busyRef.current = false;
    setBusy(false);
    if (ok) {
      markUnlocked();
      return;
    }
    setError('wrongCode');
    setCode('');
    inputRef.current?.focus();
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="card w-full max-w-sm text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center mb-4">
          <Lock size={26} className="text-primary-600 dark:text-primary-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('diaryLock.lockedTitle')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('diaryLock.lockedHint')}</p>

        {wantsBio && (
          <button
            type="button"
            onClick={tryBiometrics}
            disabled={busy}
            className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <ScanFace size={18} />
            {t('diaryLock.useFaceId')}
          </button>
        )}

        {codeSet && (
          <form onSubmit={submitCode} className={wantsBio ? 'mt-4' : 'mt-5'}>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={MAX_CODE_LENGTH}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, MAX_CODE_LENGTH));
                if (error === 'wrongCode') setError(null);
              }}
              placeholder="••••"
              aria-label={t('diaryLock.code')}
              autoFocus={!wantsBio}
              className="input text-center text-lg tracking-[0.4em]"
            />
            <button
              type="submit"
              disabled={busy || !isValidCode(code)}
              className={`w-full mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                wantsBio ? 'btn-secondary' : 'btn-primary'
              }`}
            >
              {t('diaryLock.unlock')}
            </button>
          </form>
        )}

        {error === 'wrongCode' && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {t('diaryLock.wrongCode')}
          </p>
        )}
        {error === 'bioFailed' && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400" role="alert">
            {codeSet ? t('diaryLock.bioFailedWithCode') : t('diaryLock.bioFailed')}
          </p>
        )}
      </div>
    </div>
  );
};

const DiaryLock = ({ settings, children }) => {
  const { t } = useTranslation();
  const lock = normalizeLock(settings?.diaryLock);
  const [unlocked, setUnlocked] = useState(isUnlocked);
  // Only known after the first attempt on this device (Face ID enrolled?).
  const [bioAvailable, setBioAvailable] = useState(null);

  useEffect(() => subscribeLockState(() => setUnlocked(isUnlocked())), []);

  const onBioUnavailable = useCallback(() => setBioAvailable(false), []);

  if (!lock.enabled) return children;

  const codeSet = hasCode(lock);
  const wantsBio = lock.biometrics && biometricsSupported() && bioAvailable !== false;

  if (!codeSet && !wantsBio) {
    return (
      <>
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <span>{t('diaryLock.noMethodBanner')}</span>
        </div>
        {children}
      </>
    );
  }

  if (unlocked) return children;

  return <LockScreen lock={lock} wantsBio={wantsBio} onBioUnavailable={onBioUnavailable} />;
};

export default DiaryLock;
